import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import { useParams, useHistory } from 'react-router-dom';
import { ACCOUNTS_PAGE } from 'routes';
import { API_ACCOUNT_SIGNATURE_DATA } from 'api/endpoints';

import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';

import { isoDateTimeToLabel } from 'helpers/helpers';
import compose from 'helpers/compose';
import Background from 'components/Layout/Background';

import { RootStore } from 'stores/rootStore';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { AccountCompleteStore } from 'stores/accountCompleteStore';
import useStyles from './styles';
import { APP_CONFIG } from 'appConfig';

interface Props {
  rootStore: RootStore;
  detailMode?: boolean;
  accountDetailStore?: AccountDetailStore;
}
interface SignatureData {
  phone: string;
  email: string;
  intermediary: string;
}

const AccountComplete: React.FC<Props> = ({ rootStore, detailMode, accountDetailStore }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const { accountId } = useParams<Params>();
  const [store] = useState(new AccountCompleteStore(rootStore, accountId, detailMode, accountDetailStore));
  const [signatureData, setSignatureData] = useState<SignatureData | undefined>(undefined);

  useEffect(() => {
    store.checkProgress();
    return function cleanup() {
      store.clearTimer();
    };
  }, [store]);

  useEffect(() => {
    (async () => {
      const response = await rootStore.stores.apiStore.get<SignatureData>(API_ACCOUNT_SIGNATURE_DATA(accountId));
      response && setSignatureData(response.data);
      rootStore.stores.uiStore.setCurrentAccountId(accountId);
    })();

    // FIXME: Can't perform a React state update on an unmounted component. after Ok
    return () => {
      rootStore.stores.uiStore.unsetCurrentAccountId();
    };
  }, [accountId, rootStore.stores.apiStore, rootStore.stores.uiStore]);

  const findCurrentStep = (detail: { [key: string]: string }) => {
    return detail ? Object.keys(detail).find((key) => detail[key] === 'X') : 0;
  };

  return (
    <Container maxWidth="md">
      <Background fullWidth>
        {!store.done ? (
          <>
            <Typography variant="h2" className={classes.title}>
              {t('accountComplete_docsProcessing')}
            </Typography>
            <Typography variant="h3" className={classes.title}>
              {t('accountComplete_docsProcessingETA')}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h2" className={classes.title}>
              {t('accountComplete_docsPrepared')}
            </Typography>

            <Typography variant="h3" className={classes.title}>
              {t('accountComplete_docsPreparedEmail')}&nbsp;
              {signatureData && <a href={`mailto:${signatureData?.email}`}>{signatureData?.email}</a>}.
            </Typography>
          </>
        )}

        <Grid container spacing={0} alignItems="center" alignContent="center" className={classes.progress}>
          <Grid item xs={2} className={classes.done}>
            {t('accountComplete_done')}
          </Grid>
          <Grid item xs={8}>
            <div className={classes.progressBar}>
              <div style={{ width: `${store.done ? 100 : store.progress?.percentage || 0}%` }}>&nbsp;</div>
            </div>
          </Grid>
          <Grid item xs={2}>
            {store.done ? '100' : store.progress?.percentage ? store.progress?.percentage : 0} %
          </Grid>
          {!store.done && (
            <Grid item xs={12} className={classes.currentStep}>
              {store.progress?.totalSteps && store.progress?.totalSteps !== '?' && (
                <>
                  <CircularProgress
                    size={16}
                    value={store.error ? 100 : undefined}
                    variant={store.error ? 'determinate' : 'indeterminate'}
                    color={store.error ? 'secondary' : 'primary'}
                  />
                  {t([
                    `complete_step_${detailMode ? 'change_' : ''}${findCurrentStep(store.progress?.detail)}`,
                    `complete_step_${findCurrentStep(store.progress?.detail)}${
                      accountDetailStore ? '_' + accountDetailStore?.status : ''
                    }`,
                    `complete_step_${findCurrentStep(store.progress?.detail)}`,
                  ])}
                </>
              )}
            </Grid>
          )}
        </Grid>

        {store.error && (
          <Grid container spacing={0}>
            <Grid item xs={12} className={classes.errors}>
              <p
                dangerouslySetInnerHTML={{
                  __html: t(`accountComplete_info_error`, {
                    interpolation: { escapeValue: false },
                  }),
                }}
              />
              {APP_CONFIG.errorDetails ? (
                <ul>
                  {store.error.map((err, i) => {
                    return (
                      <li key={`err_${i}`}>
                        <strong>
                          {err.type ? `${err.type}: ` : ''}
                          {err.key}
                        </strong>
                        <br />
                        <span>{err.message}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p>
                  <strong>{t('general_error_title')}</strong>
                  <br />
                  {store.error.map((err, i) => {
                    return (
                      <React.Fragment key={`err_${i}`}>
                        {t('general_account')}: {accountId}
                        <br />
                        {t('general_error_id')}: {err.occurrenceId}
                        <br />
                        {t('general_error_time')}:{' '}
                        {err.timestamp && isoDateTimeToLabel(err.timestamp, 'dd. MM. yyyy HH:mm:ss')}
                        <br />
                      </React.Fragment>
                    );
                  })}
                </p>
              )}
            </Grid>
            <Grid container item xs={12} className={classes.actions}>
              <Button variant="contained" color="primary" onClick={() => store.reinitialize()}>
                {t('accountComplete_tryAgain')}
              </Button>
            </Grid>
          </Grid>
        )}

        {store.done && (
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              dangerouslySetInnerHTML={{
                __html: t(`accountComplete_info_done`, {
                  intermediary: signatureData?.intermediary
                    ? t(`intermediary_${signatureData?.intermediary}_short`)
                    : '',
                  interpolation: { escapeValue: false },
                }),
              }}
            />
            <Grid container item xs={12} className={classes.actions}>
              {detailMode ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => accountDetailStore && accountDetailStore.stopProcessing()}
                >
                  {t('general_ok')}
                </Button>
              ) : (
                <Button variant="contained" color="primary" onClick={() => history.push(ACCOUNTS_PAGE)}>
                  {t('pageTitle_accounts')}
                </Button>
              )}
            </Grid>
          </Grid>
        )}
      </Background>
    </Container>
  );
};

export default compose(inject('rootStore'), observer)(AccountComplete);
